import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ShieldCheck,
  Sparkles
} from 'lucide-react';
import { adminApi } from '@/utils/adminApi';

export default function AdminLoginPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await adminApi.login(formData.email, formData.password);
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin_profile', JSON.stringify(response.data.admin));
      toast.success(`Welcome back, ${response.data.admin?.name || 'Super Admin'}`);
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.message || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  const formCardBase =
    'group rounded-2xl border border-slate-200 bg-white px-4 py-3 transition-all focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-100';

  const checkpoints = [
    'Create owner accounts on behalf of users',
    'Toggle owner access instantly',
    'See platform-wide PG and tenant data'
  ];

  return (
    <div className="min-h-screen bg-[#f6f7f2] text-slate-900">
      <div className="absolute inset-x-0 top-0 h-[520px] overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-dot-grid opacity-60" />
        <div className="absolute left-[-10%] top-[-5%] h-64 w-64 rounded-full bg-emerald-200/45 blur-3xl animate-pulse-soft" />
        <div className="absolute right-[5%] top-[8%] h-56 w-56 rounded-full bg-cyan-200/35 blur-3xl animate-pulse-soft animation-delay-300" />
      </div>

      <div className="relative min-h-screen px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/85 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>

          <div className="hidden items-center gap-3 sm:flex">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 shadow-[0_12px_30px_rgba(15,23,42,0.16)]">
              <Sparkles className="h-5 w-5 text-emerald-300" />
            </div>
            <div className="text-left">
              <p className="text-lg font-bold tracking-tight text-slate-950">Solucate</p>
              <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Platform Admin</p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 grid max-w-7xl gap-8 lg:mt-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="order-2 animate-fade-up lg:order-1">
            <div className="max-w-xl">
              <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-emerald-200 bg-white/85 px-4 py-2 text-sm font-medium text-emerald-800 shadow-sm">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse-soft" />
                Solucate super admin access
              </div>

              <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl">
                Run the platform from one clean control center.
              </h1>

              <p className="mt-5 text-lg leading-8 text-slate-600">
                Access owner management, platform-wide occupancy visibility, and growth metrics from a responsive super admin workspace built for Solucate.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {checkpoints.map((item, index) => (
                  <div
                    key={item}
                    className={`rounded-2xl border border-slate-200 bg-white/85 p-4 text-sm font-medium text-slate-700 shadow-sm ${index === 1 ? 'animate-fade-up animation-delay-150' : index === 2 ? 'animate-fade-up animation-delay-300' : 'animate-fade-up'}`}
                  >
                    <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950">
                    <ShieldCheck className="h-5 w-5 text-emerald-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">High-trust access</p>
                    <p className="text-lg font-semibold text-slate-950">Restricted to platform operators</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  This login opens the super admin workspace only. Regular PG owners should continue using the main Solucate login.
                </p>
              </div>
            </div>
          </div>

          <div className="order-1 animate-fade-up animation-delay-150 lg:order-2">
            <div className="glass-panel overflow-hidden rounded-[30px] border border-white/70 p-3 shadow-[0_30px_80px_rgba(15,23,42,0.12)] sm:p-4">
              <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                <div className="border-b border-slate-100 pb-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">Super Admin Login</p>
                  <h2 className="mt-1 text-2xl font-bold text-slate-950">Access platform management</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    Sign in to manage owners, platform data, and operational visibility across Solucate.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <div>
                    <Label className="mb-2 block text-sm font-semibold text-slate-700">Email</Label>
                    <div className={formCardBase}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600">
                          <Mail className="h-4 w-4" />
                        </div>
                        <Input
                          type="email"
                          placeholder="admin@solucate.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                          data-testid="admin-email"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block text-sm font-semibold text-slate-700">Password</Label>
                    <div className={`group ${formCardBase}`}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600">
                          <Lock className="h-4 w-4" />
                        </div>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="flex-1 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                          data-testid="admin-password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((value) => !value)}
                          className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="mt-2 h-14 w-full rounded-2xl bg-slate-950 text-base font-semibold text-white hover:bg-slate-800"
                    disabled={loading}
                    data-testid="admin-login-button"
                  >
                    {loading ? 'Signing in...' : 'Enter control center'}
                    {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
